import { type GetLookupResponse, LookupValueResponse } from './../../models/lookup.models';
import { autoinject } from "aurelia-dependency-injection";
import { LookupValueRequest} from "../../models/lookup.models";
import { BindingEngine } from "aurelia-binding";
import { RequestClientService } from "../basic/request-client.service";
import { CacheClientService, CacheLocation } from "../basic/client-caching.service";
import { ArrayHelper } from '../../helpers/array.helper';
import { DateTimeHelper } from '../../helpers/date-time.helper';
import { LookupUrls } from '../../constants/lookup.constants';
import { GuidHelper } from '../../helpers/guid.helper';
import { ConfigService } from '../basic/config.service';

@autoinject
export class LookupClientService {

    public cacheFlag: { flag: string, response: GetLookupResponse, successfull: boolean, subscribers: any[], expiryTimer: any, expiryTimeInSeconds: number }[] = [];
    private subscriptions: { query: string, params: string, func: () => void }[] = [];

    constructor(private requestClient: RequestClientService, private bindingEngine: BindingEngine,
        private cachingService: CacheClientService,
        private configService: ConfigService) {
    }

    subscribeForLookupsRefresh(query, params, func) {
        if (!this.subscriptions) {
            this.subscriptions = [];
        }
        this.subscriptions.push({ query, params, func });
    }

    unsubscribeForLookupsRefresh(query, params, func) {
        if (this.subscriptions) {
            const index = ArrayHelper.findIndex(this.subscriptions, x => x.func == func && x.query == query && x.params == params);
            if (index > -1) {
                this.subscriptions.splice(index, 1);
            }
        }
    }

    public getLookup(getLookupRequest: LookupValueRequest, cacheFlagKey: string = "default", expiryTimeInSeconds: number = 20): Promise<LookupValueResponse> {
        return new Promise<LookupValueResponse>((resolve, reject) => {
            if (getLookupRequest) {
                const found = this.cacheFlag.filter(x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey))[0];
                if (!found) { //We are NOT currently doing an identical call to the server, proceed
                    if (cacheFlagKey) {
                        this.cacheFlag.push({ flag: this.lookupRequestToString(getLookupRequest, cacheFlagKey), response: null, expiryTimer: null, expiryTimeInSeconds, successfull: null, subscribers: [] });
                    }
                    this.getData(getLookupRequest, cacheFlagKey).then(x => {
                        resolve(x);
                    }).catch(reject);
                } else { // We have an identical call currently processing, wait for it and return that data
                    this.waitForData(getLookupRequest, cacheFlagKey).then(x => {
                        resolve(new LookupValueResponse(x));
                    }).catch(reject);
                }
            } 
            else {
                reject();
            }
        });
    }

    private getData(getLookupRequest: LookupValueRequest, cacheFlagKey: string): Promise<LookupValueResponse> {
        return new Promise<LookupValueResponse>((resolve, reject) => {
            this.cachingService.get<GetLookupResponse>(getLookupRequest.getCacheKey(), CacheLocation.LocalStorage, false).then(cachedResponse => {
                if (!cachedResponse || !cachedResponse.values) {
                    cachedResponse = {
                        lastModifiedDate: null,
                        values: [],
                        lookupValueQuery: getLookupRequest.lookupValueQuery,
                        parameters: getLookupRequest.parameters,
                        defaultValue: null
                    }
                }
                getLookupRequest.clientLastModifiedDate = cachedResponse.lastModifiedDate;
                this.doRequest(getLookupRequest).then((response) => {
                    const func = () => {
                        const index = ArrayHelper.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey));
                        const cacheFlag = this.cacheFlag[index];
                        if (index > -1) {
                            cacheFlag.response = cachedResponse;
                            if (cacheFlag.subscribers.length == 0) {
                                this.cacheFlag.splice(index, 1);
                            }
                            cacheFlag.successfull = true;
                        }
                        resolve(new LookupValueResponse(cachedResponse));
                    };
                    if (!cachedResponse.lastModifiedDate || DateTimeHelper.castDate(response.lastModifiedDate) > DateTimeHelper.castDate(cachedResponse.lastModifiedDate)) {
                        this.cachingService.put(getLookupRequest.getCacheKey(), response).then(() => {
                            cachedResponse = response;
                            func();
                        })
                    } else {
                        func();
                    }
                }).catch(() => {
                    const index = ArrayHelper.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey));
                    const cacheFlag = this.cacheFlag[index];
                    if (index > -1) {
                        cacheFlag.response = null;
                        if (cacheFlag.subscribers.length == 0) {
                            this.cacheFlag.splice(index, 1);
                        }
                        cacheFlag.successfull = false;
                    }
                    reject();
                });
            });
        });
    }

    private waitForData(getLookupRequest: LookupValueRequest, cacheFlagKey: string): Promise<GetLookupResponse> {
        return new Promise<GetLookupResponse>((resolve, reject) => {
            const guid = GuidHelper.generateGuid();
            const index = ArrayHelper.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey));
            const cacheFlag = this.cacheFlag[index];
            if (cacheFlag.successfull != null) {
                if (cacheFlag.successfull) {
                    resolve(cacheFlag.response);
                } else {

                    reject(cacheFlag.response);
                }
                return;
            }
            cacheFlag.subscribers.push(guid);
            const subscription = this.bindingEngine.propertyObserver(cacheFlag, "successfull")
                .subscribe(newValue => {
                    subscription.dispose();
                    if (newValue) {
                        resolve(cacheFlag.response);
                    } else {

                        reject(cacheFlag.response);
                    }
                    cacheFlag.subscribers.splice(cacheFlag.subscribers.indexOf(guid), 1);
                    if (cacheFlag.subscribers.length == 0) {
                        cacheFlag.expiryTimer = setTimeout(() => {
                            cacheFlag.expiryTimer = null;
                            this.cacheFlag.splice(ArrayHelper.findIndex(this.cacheFlag, x => x.flag == this.lookupRequestToString(getLookupRequest, cacheFlagKey)), 1);
                        }, cacheFlag.expiryTimeInSeconds * 1000);
                    }
                });
        });
    }

    private doRequest(getLookupRequest: LookupValueRequest): Promise<GetLookupResponse> {
        return this.requestClient.get<GetLookupResponse>(`${this.configService.config.urls.rightShipPlatformApiUrl}/api/v1/${LookupUrls.GetLookup}`, getLookupRequest);
    }

    private lookupRequestToString(request: LookupValueRequest, cacheFlagKey: string): string {
        return request.lookupValueQuery + " " + request.parameters + " " + (cacheFlagKey ? (" " + cacheFlagKey) : "");
    }
}
